import React, { useState, useEffect, useMemo, useRef } from 'react';

// Original wrap photos — restored after user feedback.
// Served from /public so they're cached locally and never depend on an
// external CDN that can fail.
const wrapImages = [
  '/wrap-front.png',
  '/wrap-rear.png',
  '/wrap-right.png',
];

const decalImages = [
  '/partial-wrap-1.png',
  '/partial-wrap-2.png',
];

// Lower-res / alternate fallbacks if a primary asset ever fails to load.
const fallbackBy = {
  '/wrap-front.png': '/wrap-left.png',
  '/wrap-right.png': '/wrap-left.png',
  '/wrap-rear.png': '/wrap-left.png',
  '/partial-wrap-1.png': '/decal-front.png',
  '/partial-wrap-2.png': '/decal-rear.png',
};

export const CarPreview = ({ type = 'full_wrap' }) => {
  const images = useMemo(
    () => (type === 'full_wrap' ? wrapImages : decalImages),
    [type]
  );
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState({});
  const timerRef = useRef(null);

  const startCycle = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (images.length ? (prev + 1) % images.length : 0));
    }, 3500);
  };

  // Reset index whenever the image set changes so we never index out of bounds.
  useEffect(() => {
    setCurrent(0);
    setLoaded({});
  }, [images]);

  // Preload every image up-front so transitions are instant and renders never miss.
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.onload = () => setLoaded((prev) => ({ ...prev, [src]: true }));
      img.onerror = () => {
        const fb = fallbackBy[src];
        if (fb) {
          const r = new Image();
          r.onload = () => setLoaded((prev) => ({ ...prev, [src]: true }));
          r.src = fb;
        }
      };
      img.src = src;
    });
  }, [images]);

  useEffect(() => {
    startCycle();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [images]);

  // Defensive: clamp current to a valid bound in case images shrinks.
  const safeIndex = images.length ? current % images.length : 0;

  return (
    <div
      className="rounded-2xl overflow-hidden border-2 border-navy/10 bg-white"
      data-testid={`car-preview-${type}`}
    >
      <div className="relative" style={{ height: '240px' }}>
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${type === 'full_wrap' ? 'Full Wrap' : 'Partial Wrap'} – view ${i + 1}`}
            loading="eager"
            decoding="async"
            fetchPriority={i === 0 ? 'high' : 'auto'}
            className="absolute inset-0 w-full h-full object-contain transition-opacity duration-700"
            style={{
              opacity: i === safeIndex && loaded[src] ? 1 : 0,
              imageRendering: 'auto',
            }}
            draggable={false}
            onError={(e) => {
              const fb = fallbackBy[src];
              if (fb && e.currentTarget.src.indexOf(fb) === -1) {
                e.currentTarget.src = fb;
              }
            }}
            data-testid={`car-preview-${type}-img-${i}`}
          />
        ))}
        {/* Skeleton while the active image preloads */}
        {!loaded[images[safeIndex]] && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-cream/40 animate-pulse"
            data-testid={`car-preview-${type}-skeleton`}
          >
            <div className="text-navy/40 text-xs">Loading…</div>
          </div>
        )}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((src, i) => (
            <button
              key={src}
              type="button"
              aria-label={`Show view ${i + 1}`}
              onClick={() => {
                setCurrent(i);
                startCycle();
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                i === safeIndex ? 'bg-navy scale-125' : 'bg-navy/25'
              }`}
              data-testid={`car-preview-${type}-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
